<script setup lang="ts">
import type { OrderGetOneData, OrderRead } from "@classes-service-types/index";
import { useMutation } from "@tanstack/vue-query";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import Message from "primevue/message";
import ProgressSpinner from "primevue/progressspinner";
import { computed, type Ref, ref, toRefs, watch } from "vue";
import MeetingPointInfo from "@/components/MeetingPointInfo/index.vue";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import AlertCircleIcon from "@/components/shared/icons/AlertCircleIcon.vue";
import RoundedCheckMark from "@/components/shared/icons/RoundedCheckMark.vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import { STATUS_OPTIONS } from "@/constants.ts";
import ordersService from "@/services/orders";
import { dialogPt, warningPt } from "./style";
import type { ApiError, OrderNewData, OrderPartialUpdateManyResponse } from "./types.ts";

defineOptions({
  name: "BulkEditModal",
});

interface Props {
  visible?: boolean;
  orders: Array<OrderRead>;
}
const props = withDefaults(defineProps<Props>(), { visible: true });

const { visible, orders } = toRefs(props);

const emit = defineEmits(["update:visible"]);

const orderNewData: Ref<OrderNewData> = ref({
  meeting_point_name: undefined,
  meeting_point_address_line_1: undefined,
  meeting_point_address_line_2: undefined,
  meeting_point_city_id: undefined,
  meeting_point_postal_code: undefined,
  meeting_point_state: undefined,
  meeting_point_instructions: undefined,
  meeting_point_lat: undefined,
  meeting_point_long: undefined,
  meeting_point_final: undefined,
  status: undefined,
});
const successfullOrders = ref<Array<OrderGetOneData>>([]);
const failedOrders = ref<Array<OrderRead>>([]);
const apiErrors = ref<ApiError[] | null>(null);

const sessionNames = computed(() => [...new Set(orders.value.map((item) => item["Program Session"]))]);
const showOrderStatusLimitMessage = computed(
  () => orders.value.length > 10 && orderNewData.value.status !== undefined,
);
const orderIds = computed(() => orders.value.map((item) => item.id));

const {
  mutate: updateOrders,
  isIdle: isUpdateOrdersIdle,
  isPending: isUpdateOrdersPending,
  isSuccess: isUpdateOrdersSuccess,
  isError: isUpdateOrdersError,
  reset: resetUpdateOrders,
} = useMutation({
  mutationKey: ["updateOrders"],
  mutationFn: () =>
    ordersService.partialUpdateMany({
      payload: { ids: orderIds.value, data: orderNewData.value },
    }),
  onSuccess: (data: { data: OrderPartialUpdateManyResponse }) => {
    successfullOrders.value = data?.data?.data || [];
    apiErrors.value = data?.data?.errors || [];
    if (successfullOrders.value.length < orders.value.length) {
      const successfulOrderIds = new Set(successfullOrders.value.map((item) => item.order.id));
      failedOrders.value = orders.value.filter((item) => !successfulOrderIds.has(item.id));
    }
  },
  onError: () => {
    failedOrders.value = [...orders.value];
  },
});

const handleMeetingPointChange = (updatedData) => {
  orderNewData.value = {
    ...orderNewData.value,
    ...updatedData,
  };
};
const submit = () => {
  updateOrders();
};
const handleCloseModal = () => {
  successfullOrders.value = [];
  failedOrders.value = [];
  orderNewData.value = {
    meeting_point_name: undefined,
    meeting_point_address_line_1: undefined,
    meeting_point_address_line_2: undefined,
    meeting_point_city_id: undefined,
    meeting_point_postal_code: undefined,
    meeting_point_state: undefined,
    meeting_point_instructions: undefined,
    meeting_point_lat: undefined,
    meeting_point_long: undefined,
    meeting_point_final: undefined,
    status: undefined,
  };
  resetUpdateOrders();
  emit("update:visible", false);
};
</script>

<template>
  <Dialog :visible="visible" modal header="Bulk Edit" :pt="dialogPt">
    <template #closeicon>
      <button class="flex h-6 w-6 items-center justify-center text-teal-900" @click="handleCloseModal">
        <CloseIcon size="28" stroke-width="1.75" />
      </button>
    </template>
    <div class="text-gray-700">
      <div v-if="isUpdateOrdersIdle">
        <p class="mb-6 text-sm">
          <span class="font-bold">Update For</span> {{ orders?.length ?? 0 }} students
          <span v-if="sessionNames.length > 1">across multiple sessions</span>
          <span v-else>with the session "{{ sessionNames?.[0] ?? "" }}"</span>
        </p>
        <div class="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
          <div class="flex flex-col gap-2">
            <label for="status" class="common-label-text">Order Status</label>
            <Dropdown
              id="status"
              v-model="orderNewData.status"
              :options="STATUS_OPTIONS"
              optionLabel="label"
              optionValue="value"
              placeholder="Select status"
              class="w-full md:w-14rem"
              showClear
            />
          </div>
          <Message v-if="showOrderStatusLimitMessage" severity="warn" :closable="false" :pt="warningPt" class="col-span-2">
            Order status can be updated for a maximum of <strong>10 orders</strong> at a time. Other meeting point
            details below have no limit.
          </Message>
        </div>
        <MeetingPointInfo :meeting-point-location="orderNewData" @update="handleMeetingPointChange" />
        <div class="flex justify-center">
          <BaseButton outlined :on-click="handleCloseModal">Cancel</BaseButton>
          <BaseButton :on-click="submit">Save</BaseButton>
        </div>
      </div>
      <div v-else-if="isUpdateOrdersPending" class="text-center">
        <p class="text-center">Updates will take a moment</p>
        <ProgressSpinner style="width: 50px; height: 50px;" />
      </div>
      <div v-else-if="isUpdateOrdersSuccess || isUpdateOrdersError">
        <p class="mb-6 text-sm">
          <span class="font-bold">Update For</span> {{ orders?.length ?? 0 }} students
          <span v-if="sessionNames.length > 1">across multiple sessions</span>
          <span v-else>with the session "{{ sessionNames?.[0] ?? "" }}"</span>
        </p>
        <div class="text-center">
          <div class="flex items-center space-x-2 justify-center mb-6">
            <RoundedCheckMark class="w-5 h-5" />
            <p>
              Updates for {{ successfullOrders?.length ?? 0 }} of {{ orders?.length ?? 0 }} students have been made
              successfully.
            </p>
          </div>
          <template v-if="failedOrders.length">
            <div class="flex items-center space-x-2 justify-center mb-6">
              <div class="text-red-600">
                <AlertCircleIcon size="20" />
              </div>
              <p>
                Updates for {{ failedOrders?.length ?? 0 }} of {{ orders?.length ?? 0 }} students have NOT been made
                successfully. They are:
              </p>
            </div>
            <template v-for="order in failedOrders" :key="order.id">
              <p>{{ order?.["Name"] ?? "" }}</p>
            </template>
          </template>
          <template v-if="apiErrors?.length">
            <div class="flex px-10 flex-col">
              <template v-for="(error, index) in apiErrors" :key="index">
                <p class="text-left text-red-600">
                  {{ error?.detail ?? "Unknown error occurred" }}
                </p>
              </template>
            </div>
          </template>
          <BaseButton outlined :on-click="handleCloseModal" class="mt-6">Close</BaseButton>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
::v-deep(.progress-spinner-circle) {
  @apply !stroke-teal-50;
}
</style>
